import { toast } from "react-toastify"
import axios from "axios"
import { v4 as uuid } from "uuid"

import { REQ } from "constants/api/requests"
import { IResponse, IResponsePayload } from "constants/interfaces"

import useApi, { parseError } from "util/api"

import {
  CategoryCreateInput,
  CategoryRecord,
  CategorySeo,
  CategoryUpdateInput,
  GameSearchParam,
  GameSectionsInterface,
  GameSectionsResponseInterface,
  HomepageOrderResponse,
  IGameService,
  SectionOrderResponse
} from "./type"

class GameService implements IGameService {
  public async getGameProviders(): Promise<void | []> {
    const { data } = await useApi.get<IResponse<[]>>(REQ.ADMIN_DASHBOARD.GAMES.FREE_SPIN_GAME_PROVIDERS)

    if (data?.success) {
      return data?.response
    }
  }

  public async getFreeSpinGames(provider?: string, query?: string): Promise<void | []> {
    const params = {
      provider: provider || null,
      query,
      // The server's max page size — this caller shows one screenful and never paged, so it
      // keeps exactly the reach it had before the endpoint was paginated.
      pageSize: 500
    }

    const { data } = await useApi.get<IResponse<{ games: [] }>>(REQ.ADMIN_DASHBOARD.GAMES.FREE_SPIN_GAMES, { params })

    if (data?.success) {
      return data?.response?.games ?? []
    }
    return []
  }

  public async getGamesByIds(params: { gameIds: string[]; includeDisabled?: boolean }): Promise<GameSectionsResponseInterface | undefined> {
    try {
      const { data } = await useApi.post<{
        success: boolean
        response: GameSectionsResponseInterface
      }>(REQ.ADMIN_DASHBOARD.GAMES.GET_BY_IDS, {
        gameIds: params.gameIds,
        includeDisabled: params.includeDisabled || false
      })

      return data?.response
    } catch (error) {
      console.error("Failed to fetch games by IDs:", error)
      toast.error("Failed to fetch specific games")
    }
  }

  public async getAllGames(params: GameSearchParam): Promise<GameSectionsResponseInterface | undefined> {
    try {
      const { data } = await useApi.get<{
        success: boolean
        response: GameSectionsResponseInterface
      }>(REQ.ADMIN_DASHBOARD.GAMES.GET_ALL, {
        params: { ...params },
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) return value.map((v) => `${key}[]=${encodeURIComponent(v)}`).join("&")
              return `${key}=${encodeURIComponent(value as string)}`
            })
            .join("&")
        }
      })

      return data?.response
    } catch (error) {
      toast.error("Failed to fetch games")
    }
  }

  public async getGameSection(params: GameSearchParam): Promise<GameSectionsResponseInterface | undefined> {
    try {
      const { data } = await useApi.get<{
        success: boolean
        response: GameSectionsResponseInterface
      }>(REQ.ADMIN_DASHBOARD.GAMES.GET_SECTION, {
        params: { ...params },
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) return value.map((v) => `${key}[]=${encodeURIComponent(v)}`).join("&")
              return `${key}=${encodeURIComponent(value as string)}`
            })
            .join("&")
        }
      })

      return data.response
    } catch (error) {
      toast.error("Failed to fetch games")
    }
  }

  public async getGamesBySection(params: GameSearchParam): Promise<GameSectionsInterface | undefined> {
    try {
      const { data } = await useApi.get<{
        success: boolean
        response: GameSectionsInterface
      }>(REQ.ADMIN_DASHBOARD.GAMES.GET_BY_SECTION, {
        params: { ...params },
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) return value.map((v) => `${key}[]=${encodeURIComponent(v)}`).join("&")
              return `${key}=${encodeURIComponent(value as string)}`
            })
            .join("&")
        }
      })

      return data?.response
    } catch (error) {
      toast.error("Failed to fetch games")
    }
  }

  public async toggleEnabled(id: string, enabled: boolean): Promise<number | null> {
    try {
      const { data } = await useApi.patch<IResponse<{ affected?: number }>>(REQ.ADMIN_DASHBOARD.GAMES.TOGGLE_ENABLED(id), { enabled })
      return data?.response?.affected ?? 1
    } catch (e) {
      toast.error("Failed to update game status")
      return null
    }
  }

  public async getSectionOrder(
    sectionKey: string,
    { page, pageSize, geo }: { page: number; pageSize: number; geo?: string }
  ): Promise<SectionOrderResponse | null> {
    try {
      const { data } = await useApi.get<IResponse<SectionOrderResponse>>(REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDER(sectionKey), {
        params: { page, pageSize, geo: geo ?? "*" }
      })
      return data?.response ?? null
    } catch {
      toast.error("Failed to load section order")
      return null
    }
  }

  public async replaceSectionOrder(sectionKey: string, gameIds: string[], geo?: string): Promise<boolean> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDER(sectionKey), {
        gameIds,
        geo: geo ?? "*"
      })
      return true
    } catch {
      toast.error("Failed to save order")
      return false
    }
  }

  /**
   * @param scope
   *   - "category_order" → forks in CategoryOrder (Homepage tab)
   *   - "game_order"     → forks in GameCategoryOrder, all categories (Sections / Categories tabs)
   *   - "game_order" + category → forks in GameCategoryOrder for one category (SectionOrderEditor drill-in)
   *   - "any" (default)  → union of both
   */
  public async listForkedGeos(scope: "category_order" | "game_order" | "any" = "any", category?: string): Promise<string[] | null> {
    try {
      const params: Record<string, string> = { scope }
      if (category) params.category = category
      const { data } = await useApi.get<IResponse<{ geos: string[] }>>(REQ.ADMIN_DASHBOARD.GAMES.FORKED_GEOS, {
        params
      })
      return data?.response?.geos ?? null
    } catch {
      return null
    }
  }

  public async getHomepageOrder(geo?: string): Promise<HomepageOrderResponse | null> {
    try {
      const { data } = await useApi.get<IResponse<HomepageOrderResponse>>(REQ.ADMIN_DASHBOARD.GAMES.HOMEPAGE_ORDER, {
        params: { geo: geo ?? "*" }
      })
      return data?.response ?? null
    } catch {
      toast.error("Failed to load homepage order")
      return null
    }
  }

  public async replaceHomepageOrder(items: { key: string; enabled: boolean }[], geo?: string): Promise<boolean> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.GAMES.HOMEPAGE_ORDER, {
        items,
        geo: geo ?? "*"
      })
      return true
    } catch {
      toast.error("Failed to save homepage order")
      return false
    }
  }

  // Three-step presigned-PUT flow (same shape as CMS images): the file goes
  // browser → S3 directly so it never passes through our API server. Step 3
  // (save) lets the server validate the key prefix and clean up the old
  // override; the FE never templates a bucket URL.
  public async uploadGameImageVerticalOverride(gameId: string, file: File): Promise<string | null> {
    try {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "bin"
      const fileName = `${uuid()}.${extension}`

      const presignRes = await useApi.post<IResponse<{ uploadUrl: string; key: string }>>(
        REQ.ADMIN_DASHBOARD.GAMES.IMAGE_VERTICAL_OVERRIDE_PRESIGN(gameId),
        { fileName }
      )
      const presigned = presignRes.data?.response
      if (!presigned?.uploadUrl || !presigned?.key) {
        toast.error("Failed to upload image")
        return null
      }

      await axios.put(presigned.uploadUrl, file, {
        headers: { "Content-Type": file.type }
      })

      const saveRes = await useApi.put<IResponse<{ ok: boolean; image_vertical_override: string }>>(
        REQ.ADMIN_DASHBOARD.GAMES.IMAGE_VERTICAL_OVERRIDE(gameId),
        { key: presigned.key }
      )
      return saveRes.data?.response?.image_vertical_override ?? null
    } catch {
      toast.error("Failed to upload image")
      return null
    }
  }

  public async resetGameImageVerticalOverride(gameId: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.GAMES.IMAGE_VERTICAL_OVERRIDE(gameId))
      return true
    } catch {
      toast.error("Failed to reset image")
      return false
    }
  }

  public async updateGameDescription(gameId: string, description: string | null): Promise<string | null | undefined> {
    try {
      const { data } = await useApi.patch<IResponse<{ ok: boolean; identifier: string; description: string | null }>>(
        REQ.ADMIN_DASHBOARD.GAMES.DESCRIPTION(gameId),
        { description }
      )
      return data?.response?.description
    } catch (err: any) {
      parseError(err, "Failed to update description")
      return undefined
    }
  }

  public async updateGameSeo(gameId: string, seo: CategorySeo): Promise<boolean> {
    try {
      const { data } = await useApi.patch<IResponse<{ ok: boolean }>>(REQ.ADMIN_DASHBOARD.GAMES.SEO(gameId), { seo })
      return Boolean(data?.success)
    } catch (err: any) {
      parseError(err, "Failed to update game SEO")
      return false
    }
  }

  public async listCategories(): Promise<CategoryRecord[] | null> {
    try {
      const { data } = await useApi.get<IResponse<{ items: CategoryRecord[] }>>(REQ.ADMIN_DASHBOARD.GAMES.CATEGORIES_LIST)
      return data?.response?.items ?? null
    } catch {
      toast.error("Failed to load categories")
      return null
    }
  }

  public async createCategory(input: CategoryCreateInput): Promise<CategoryRecord | null> {
    try {
      const { data } = await useApi.post<IResponse<CategoryRecord>>(REQ.ADMIN_DASHBOARD.GAMES.CATEGORIES_LIST, input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to create category")
      return null
    }
  }

  public async updateCategory(key: string, patch: CategoryUpdateInput): Promise<CategoryRecord | null> {
    try {
      const { data } = await useApi.patch<IResponse<CategoryRecord>>(REQ.ADMIN_DASHBOARD.GAMES.CATEGORY_BY_KEY(key), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to update category")
      return null
    }
  }

  public async deleteCategory(key: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.GAMES.CATEGORY_BY_KEY(key))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete category")
      return false
    }
  }

  // Download a CSV blob and trigger a browser download. Shared by both CSV
  // exports — same flow as providersService.exportFeesCsv.
  private async downloadCsv(url: string, filename: string, errorMsg: string): Promise<boolean> {
    try {
      const { data } = await useApi.get<Blob>(url, { responseType: "blob" })
      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" })
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = objectUrl
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
      return true
    } catch {
      toast.error(errorMsg)
      return false
    }
  }

  public async exportHomepageOrderCsv(): Promise<boolean> {
    return this.downloadCsv(
      REQ.ADMIN_DASHBOARD.GAMES.HOMEPAGE_ORDER_EXPORT_CSV,
      "homepage-section-order.csv",
      "Failed to export homepage section order"
    )
  }

  public async importHomepageOrderCsv(file: File): Promise<{ geos: number; rows: number } | null> {
    try {
      const formData = new FormData()
      formData.append("file", file)
      const { data } = await useApi.post<IResponse<{ geos: number; rows: number }>>(
        REQ.ADMIN_DASHBOARD.GAMES.HOMEPAGE_ORDER_IMPORT_CSV,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      if (data?.success && data.response) {
        const { geos, rows } = data.response
        toast.success(`Imported ${rows} rows across ${geos} geo(s)`)
        return data.response
      }
      toast.error("Failed to import homepage section order")
      return null
    } catch (err: any) {
      parseError(err, "Failed to import homepage section order")
      return null
    }
  }

  public async exportSectionGameOrdersCsv(): Promise<boolean> {
    return this.downloadCsv(
      REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDERS_EXPORT_CSV,
      "section-game-orders.csv",
      "Failed to export section game orders"
    )
  }

  public async importSectionGameOrdersCsv(file: File): Promise<{
    groups: number
    rows: number
    unknown_games_skipped: number
  } | null> {
    try {
      const formData = new FormData()
      formData.append("file", file)
      const { data } = await useApi.post<IResponse<{ groups: number; rows: number; unknown_games_skipped: number }>>(
        REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDERS_IMPORT_CSV,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      )
      if (data?.success && data.response) {
        const { groups, rows, unknown_games_skipped } = data.response
        const skippedNote = unknown_games_skipped > 0 ? ` (${unknown_games_skipped} unknown game id(s) skipped)` : ""
        toast.success(`Imported ${rows} rows across ${groups} (category, geo) group(s)${skippedNote}`)
        return data.response
      }
      toast.error("Failed to import section game orders")
      return null
    } catch (err: any) {
      parseError(err, "Failed to import section game orders")
      return null
    }
  }

  // Per-section variant — narrows the bulk endpoints to one Category.key.
  // Used from inside SectionOrderEditor (the drill-in view) so admins can
  // round-trip just the section they're editing.
  public async exportSectionOrderCsv(sectionKey: string): Promise<boolean> {
    return this.downloadCsv(
      REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDER_EXPORT_CSV(sectionKey),
      `section-game-orders-${sectionKey}.csv`,
      "Failed to export section order"
    )
  }

  public async importSectionOrderCsv(
    sectionKey: string,
    file: File
  ): Promise<{ groups: number; rows: number; unknown_games_skipped: number } | null> {
    try {
      const formData = new FormData()
      formData.append("file", file)
      const { data } = await useApi.post<IResponse<{ groups: number; rows: number; unknown_games_skipped: number }>>(
        REQ.ADMIN_DASHBOARD.GAMES.SECTION_ORDER_IMPORT_CSV(sectionKey),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      )
      if (data?.success && data.response) {
        const { rows, unknown_games_skipped } = data.response
        const skippedNote = unknown_games_skipped > 0 ? ` (${unknown_games_skipped} unknown game id(s) skipped)` : ""
        toast.success(`Imported ${rows} rows for "${sectionKey}"${skippedNote}`)
        return data.response
      }
      toast.error("Failed to import section order")
      return null
    } catch (err: any) {
      parseError(err, "Failed to import section order")
      return null
    }
  }

  public async getUpdateStatus(): Promise<{
    lastManualRunAt: string | null
    lastCronRunAt: string | null
    nextAllowedAt: string | null
    inProgress: boolean
    allowed: boolean
  } | null> {
    try {
      const { data } = await useApi.get<IResponsePayload<any>>(REQ.ADMIN_DASHBOARD.GAMES.UPDATE_STATUS)
      return data?.response.payload ?? null
    } catch {
      toast.error("Failed to fetch update status")
      return null
    }
  }

  public async triggerUpdate(): Promise<{ ok: boolean; nextAllowedAt?: string | null; inProgress?: boolean } | null> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.GAMES.TRIGGER_UPDATE, {})
      return data?.response ?? null
    } catch {
      toast.error("Failed to trigger games update")
      return null
    }
  }
}

export const gameService = new GameService()
